import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { SERVICES } from "@/data/services";
import { Mail, Phone, MapPin, Send, CheckCircle2 } from "lucide-react";
import { toast, Toaster } from "sonner";

const BUDGETS = ["< $2,000", "$2,000 – $5,000", "$5,000 – $15,000", "$15,000 – $50,000", "$50,000+"];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", service: "", budget: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name || !form.email || !form.message) { setError("Name, email, and message are required."); return; }
    setLoading(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Message sent! We'll be in touch shortly.");
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally { setLoading(false); }
  };

  return (
    <div>
      <Toaster position="top-right" />
      <section className="border-b border-brand-border">
        <div className="container-x pt-20 pb-16 lg:pt-28 lg:pb-20">
          <div className="max-w-3xl">
            <div className="eyebrow mb-3">Contact</div>
            <h1 className="font-display font-black text-5xl sm:text-6xl tracking-tight" data-testid="contact-title">Let's build something great together.</h1>
            <p className="mt-6 text-lg text-brand-muted">Share a few details about your project. We'll get back within one business day.</p>
          </div>
        </div>
      </section>

      <section className="container-x py-16 lg:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="space-y-6">
              <div>
                <div className="text-xs uppercase tracking-widest text-brand-muted mb-1">Email</div>
                <a href="mailto:support@telstranics.com" data-testid="contact-email" className="text-lg font-medium text-brand-ink hover:text-brand-blue flex items-center gap-2"><Mail className="w-4 h-4" /> support@telstranics.com</a>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-brand-muted mb-1">Phone</div>
                <a href="tel:+18045655569" data-testid="contact-phone" className="text-lg font-medium text-brand-ink hover:text-brand-blue flex items-center gap-2"><Phone className="w-4 h-4" /> +1 (804) 565-5569</a>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-brand-muted mb-1">Studio</div>
                <div className="text-base font-medium text-brand-ink flex items-start gap-2" data-testid="contact-address"><MapPin className="w-4 h-4 mt-1 shrink-0" /> <span>1207 Delaware Ave #2392<br />Wilmington, DE 19806<br />United States</span></div>
              </div>
              <div className="p-5 bg-brand-surface rounded-2xl border border-brand-border">
                <div className="text-sm font-medium text-brand-ink">Response time</div>
                <div className="text-sm text-brand-muted mt-1">Within 1 business day, typically much sooner.</div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-8">
            {sent ? (
              <div className="bg-white border border-brand-border rounded-3xl p-12 text-center" data-testid="contact-success">
                <div className="w-16 h-16 rounded-full bg-brand-blue/10 text-brand-blue flex items-center justify-center mx-auto mb-6">
                  <CheckCircle2 className="w-8 h-8" />
                </div>
                <h2 className="font-display font-bold text-3xl text-brand-ink">Message received.</h2>
                <p className="mt-3 text-brand-muted">Thanks {form.name || "there"} — we'll respond to <span className="text-brand-ink font-medium">{form.email}</span> shortly.</p>
                <button data-testid="contact-send-another" onClick={() => { setSent(false); setForm({ name: "", email: "", phone: "", company: "", service: "", budget: "", message: "" }); }} className="btn-outline mt-8">Send Another Message</button>
              </div>
            ) : (
              <form onSubmit={submit} className="bg-white border border-brand-border rounded-3xl p-8 sm:p-10 space-y-5" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Name *" name="name">
                    <input required data-testid="contact-input-name" value={form.name} onChange={set("name")} className="input" placeholder="Jane Doe" />
                  </Field>
                  <Field label="Email *" name="email">
                    <input required type="email" data-testid="contact-input-email" value={form.email} onChange={set("email")} className="input" placeholder="jane@company.com" />
                  </Field>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Phone" name="phone">
                    <input data-testid="contact-input-phone" value={form.phone} onChange={set("phone")} className="input" placeholder="Optional" />
                  </Field>
                  <Field label="Company" name="company">
                    <input data-testid="contact-input-company" value={form.company} onChange={set("company")} className="input" placeholder="Company name" />
                  </Field>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Service of interest" name="service">
                    <select data-testid="contact-input-service" value={form.service} onChange={set("service")} className="input">
                      <option value="">Select a service</option>
                      {SERVICES.map((s) => <option key={s.slug} value={s.title}>{s.title}</option>)}
                      <option value="Other">Other / Not sure</option>
                    </select>
                  </Field>
                  <Field label="Budget range" name="budget">
                    <select data-testid="contact-input-budget" value={form.budget} onChange={set("budget")} className="input">
                      <option value="">Select budget</option>
                      {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
                    </select>
                  </Field>
                </div>
                <Field label="Tell us about your project *" name="message">
                  <textarea required data-testid="contact-input-message" value={form.message} onChange={set("message")} className="input min-h-[160px] resize-y" placeholder="What are you building? Goals, timeline, current challenges..." />
                </Field>

                {error && <div className="text-sm text-red-600" data-testid="contact-error">{error}</div>}

                <button disabled={loading} data-testid="contact-submit" type="submit" className="btn-primary w-full sm:w-auto disabled:opacity-60">
                  {loading ? "Sending..." : (<>Send Message <Send className="w-4 h-4" /></>)}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-brand-ink mb-1.5 block">{label}</span>
      {children}
      <style>{`.input{width:100%;padding:0.75rem 1rem;border:1px solid #E5E7EB;border-radius:0.75rem;font-size:0.95rem;background:#fff;transition:border-color .15s, box-shadow .15s}.input:focus{outline:none;border-color:#0A0A0A;box-shadow:0 0 0 4px rgba(37,99,235,0.1)}`}</style>
    </label>
  );
}
